import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Flame, Snowflake, RefreshCw, Users, Database, Loader2, Heart, ImageOff, Brain, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatusPill = ({ status }) => {
  const map = {
    warm: { bg: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: <Flame className="h-3 w-3" />, label: "Warm" },
    partial: { bg: "bg-amber-100 text-amber-800 border-amber-200", icon: <Loader2 className="h-3 w-3" />, label: "Partial" },
    cold: { bg: "bg-blue-100 text-blue-800 border-blue-200", icon: <Snowflake className="h-3 w-3" />, label: "Cold" },
  };
  const s = map[status] || map.cold;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${s.bg}`}
      data-testid={`combo-status-${status}`}
    >
      {s.icon}
      {s.label}
    </span>
  );
};

const AdminMealCoverageTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [warmingCombo, setWarmingCombo] = useState(null);
  const [runningUserWarmup, setRunningUserWarmup] = useState(false);
  const [refreshingStale, setRefreshingStale] = useState(false);
  const [error, setError] = useState(null);
  // Slot-fill / image audit / adaptive learning state
  const [slotAudit, setSlotAudit] = useState(null);
  const [healing, setHealing] = useState(false);
  const [imageAudit, setImageAudit] = useState(null);
  const [scrubbing, setScrubbing] = useState(false);
  const [trending, setTrending] = useState(null);
  const [runningAdaptive, setRunningAdaptive] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);

  const token = localStorage.getItem("auth_token");

  const authFetch = useCallback(
    (path, opts = {}) =>
      fetch(`${BACKEND_URL}${path}`, {
        ...opts,
        headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
      }),
    [token]
  );

  const fetchCoverage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/meals/admin/diet-coverage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCoverage();
  }, [fetchCoverage]);

  const fetchSlotAudit = useCallback(async () => {
    try {
      const res = await authFetch(`/api/meals/admin/slot-fill-audit?diet_key=none&days=14`);
      if (res.ok) setSlotAudit(await res.json());
    } catch (e) {
      // non-fatal
    }
  }, [authFetch]);

  const fetchImageAudit = useCallback(async () => {
    try {
      const res = await authFetch(`/api/meals/admin/bad-image-audit`);
      if (res.ok) setImageAudit(await res.json());
    } catch (e) {
      // non-fatal
    }
  }, [authFetch]);

  const fetchTrending = useCallback(async () => {
    try {
      const res = await authFetch(`/api/meals/admin/trending-diet-combos?lookback_days=7`);
      if (res.ok) setTrending(await res.json());
    } catch (e) {
      // non-fatal
    }
  }, [authFetch]);

  useEffect(() => {
    fetchSlotAudit();
    fetchImageAudit();
    fetchTrending();
  }, [fetchSlotAudit, fetchImageAudit, fetchTrending]);

  const healUnderfilledSlots = async () => {
    setHealing(true);
    setActionMessage(null);
    try {
      const res = await authFetch(
        `/api/meals/admin/heal-underfilled-slots?diet_key=none&days=14&target_min=10&count=10`,
        { method: "POST" }
      );
      const j = await res.json();
      const n = j.underfilled_count ?? 0;
      setActionMessage(
        n === 0
          ? "All 'none' diet slots already at full strength — no action needed."
          : `Healing ${n} underfilled slot(s) in the background. Check back in ~2 minutes.`
      );
      // Re-fetch audit after a delay so the user sees the result
      setTimeout(() => {
        fetchSlotAudit();
        fetchCoverage();
        setHealing(false);
      }, 90000);
    } catch (e) {
      setError(String(e));
      setHealing(false);
    }
  };

  const scrubBadImages = async () => {
    setScrubbing(true);
    setActionMessage(null);
    try {
      const res = await authFetch(
        `/api/meals/admin/scrub-bad-images?refetch_emptied=true`,
        { method: "POST" }
      );
      const j = await res.json();
      setActionMessage(
        j.recipes_dropped === 0
          ? "Cache image health is perfect — no recipes needed scrubbing."
          : `Scrubbed ${j.recipes_dropped} bad-image recipe(s) across ${j.docs_modified + j.docs_deleted} cache doc(s). Refetching ${j.refetches_scheduled} emptied slot(s).`
      );
      setTimeout(() => {
        fetchImageAudit();
        fetchCoverage();
        setScrubbing(false);
      }, 60000);
    } catch (e) {
      setError(String(e));
      setScrubbing(false);
    }
  };

  const runAdaptiveLearning = async () => {
    setRunningAdaptive(true);
    setActionMessage(null);
    try {
      await authFetch(
        `/api/meals/admin/run-adaptive-learning?lookback_days=7&top_n=5&min_request_count=3`,
        { method: "POST" }
      );
      setActionMessage(
        "Adaptive learning sweep started. The cache is now mining recent request patterns and warming the top trending combos. Email summary will arrive on completion (1-3 minutes)."
      );
      setTimeout(() => {
        fetchTrending();
        fetchCoverage();
        setRunningAdaptive(false);
      }, 120000);
    } catch (e) {
      setError(String(e));
      setRunningAdaptive(false);
    }
  };

  const warmCombo = async (restrictions) => {
    const key = restrictions.join(",");
    setWarmingCombo(key);
    try {
      await fetch(
        `${BACKEND_URL}/api/meals/admin/warm-combo?restrictions=${encodeURIComponent(key)}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      // Give background task a moment then refresh
      setTimeout(() => {
        fetchCoverage();
        setWarmingCombo(null);
      }, 90000);
    } catch (e) {
      setError(String(e));
      setWarmingCombo(null);
    }
  };

  const runFullUserWarmup = async () => {
    setRunningUserWarmup(true);
    try {
      await fetch(`${BACKEND_URL}/api/meals/admin/run-user-warmup`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      // Full run can take several minutes; poll every 30s for 5 min
      let polls = 0;
      const interval = setInterval(() => {
        polls += 1;
        fetchCoverage();
        if (polls >= 10) {
          clearInterval(interval);
          setRunningUserWarmup(false);
        }
      }, 30000);
    } catch (e) {
      setError(String(e));
      setRunningUserWarmup(false);
    }
  };

  const refreshStale = async () => {
    setRefreshingStale(true);
    try {
      await fetch(`${BACKEND_URL}/api/meals/admin/refresh-stale?max_entries=30`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      setTimeout(() => {
        fetchCoverage();
        setRefreshingStale(false);
      }, 60000);
    } catch (e) {
      setError(String(e));
      setRefreshingStale(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading diet coverage…
      </div>
    );
  }

  if (error) {
    return <div className="text-red-600 text-sm p-4" data-testid="meal-coverage-error">Error: {error}</div>;
  }

  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="meal-coverage-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
              <Users className="h-3 w-3" /> Total Users
            </div>
            <div className="text-2xl font-bold" data-testid="coverage-total-users">{data.total_users}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
              <Users className="h-3 w-3" /> With Diet Prefs
            </div>
            <div className="text-2xl font-bold" data-testid="coverage-users-with-prefs">{data.users_with_diet_prefs}</div>
            <div className="text-xs text-gray-500 mt-0.5">{data.unique_combos} unique combos</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
              <Database className="h-3 w-3" /> Default Cache
            </div>
            <div className="text-2xl font-bold" data-testid="coverage-default-pct">{data.default_cache.coverage_pct}%</div>
            <div className="mt-1"><StatusPill status={data.default_cache.status} /></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
              <Snowflake className="h-3 w-3" /> Stale (&gt;30d)
            </div>
            <div className="text-2xl font-bold" data-testid="coverage-stale-entries">{data.stale_entries ?? 0}</div>
            <div className="text-xs text-gray-500 mt-0.5">entries to rotate</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
              <RefreshCw className="h-3 w-3" /> Fetches (7d)
            </div>
            <div className="text-2xl font-bold" data-testid="coverage-recent-fetches">{data.recent_meal_fetches_7d}</div>
          </CardContent>
        </Card>
      </div>

      {/* Source breakdown */}
      {data.source_breakdown && Object.keys(data.source_breakdown).length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="source-breakdown">
          <span className="text-gray-500">Recipe sources:</span>
          {Object.entries(data.source_breakdown)
            .sort(([, a], [, b]) => b - a)
            .map(([src, count]) => (
              <span
                key={src}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700 border-gray-200 capitalize"
              >
                {src}
                <span className="font-semibold">{count}</span>
              </span>
            ))}
        </div>
      )}

      {/* Action Bar */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={fetchCoverage}
          disabled={loading}
          data-testid="coverage-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        <Button
          size="sm"
          onClick={runFullUserWarmup}
          disabled={runningUserWarmup}
          data-testid="coverage-run-full-warmup-btn"
        >
          {runningUserWarmup ? (
            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Warming all combos…</>
          ) : (
            <><Flame className="h-4 w-4 mr-1" /> Warm all user combos</>
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={refreshStale}
          disabled={refreshingStale || (data.stale_entries ?? 0) === 0}
          data-testid="coverage-refresh-stale-btn"
        >
          {refreshingStale ? (
            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Refreshing stale…</>
          ) : (
            <><Snowflake className="h-4 w-4 mr-1" /> Refresh {data.stale_entries ?? 0} stale</>
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={healUnderfilledSlots}
          disabled={healing}
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid="coverage-heal-slots-btn"
        >
          {healing ? (
            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Healing slots…</>
          ) : (
            <><Heart className="h-4 w-4 mr-1" /> Heal underfilled slots</>
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={scrubBadImages}
          disabled={scrubbing}
          className="border-rose-300 text-rose-700 hover:bg-rose-50"
          data-testid="coverage-scrub-images-btn"
        >
          {scrubbing ? (
            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Scrubbing…</>
          ) : (
            <><ImageOff className="h-4 w-4 mr-1" /> Scrub bad images</>
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={runAdaptiveLearning}
          disabled={runningAdaptive}
          className="border-purple-300 text-purple-700 hover:bg-purple-50"
          data-testid="coverage-run-adaptive-btn"
        >
          {runningAdaptive ? (
            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Learning…</>
          ) : (
            <><Brain className="h-4 w-4 mr-1" /> Run adaptive learning</>
          )}
        </Button>
      </div>

      {actionMessage && (
        <div
          className="text-sm bg-blue-50 text-blue-800 border border-blue-200 rounded-lg p-3"
          data-testid="coverage-action-message"
        >
          {actionMessage}
        </div>
      )}

      {/* Self-Healing Panel: Slot Fill + Image Health + Audience Trends */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Slot fill audit */}
        <Card data-testid="slot-fill-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-2">
              <Heart className="h-3 w-3" /> Slot Fill (none diet)
            </div>
            {slotAudit ? (
              <>
                <div className="text-xl font-bold text-emerald-600" data-testid="slot-fill-full">
                  {slotAudit.fully_filled_10plus}/{slotAudit.total_slots}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  full ({slotAudit.empty_slots} empty · {slotAudit.underfilled_lt_5 + slotAudit.underfilled_5_to_9} underfilled)
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-400">Loading…</div>
            )}
          </CardContent>
        </Card>

        {/* Image health audit */}
        <Card data-testid="image-health-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-2">
              <ImageOff className="h-3 w-3" /> Image Health
            </div>
            {imageAudit ? (
              <>
                <div
                  className={`text-xl font-bold ${
                    imageAudit.bad_image_recipes === 0 ? "text-emerald-600" : "text-rose-600"
                  }`}
                  data-testid="image-bad-count"
                >
                  {imageAudit.bad_image_recipes}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  bad of {imageAudit.total_recipes} ({imageAudit.bad_pct}%)
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-400">Loading…</div>
            )}
          </CardContent>
        </Card>

        {/* Audience trends */}
        <Card data-testid="audience-trends-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs mb-2">
              <TrendingUp className="h-3 w-3" /> Audience Trends (7d)
            </div>
            {trending && trending.top_combos && trending.top_combos.length > 0 ? (
              <div className="space-y-1">
                {trending.top_combos.slice(0, 3).map((c) => (
                  <div
                    key={c.combo_key}
                    className="flex items-center justify-between text-xs"
                    data-testid={`trending-combo-${c.combo_key}`}
                  >
                    <span className="font-mono text-gray-700 truncate mr-2">
                      {c.combo_key === "none" ? "(no diet)" : c.combo_key}
                    </span>
                    <span className="text-gray-500 whitespace-nowrap">
                      {c.request_count}× / {c.unique_users}u
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-400">No traffic yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Combo Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Diet/Allergy Combos by Coverage</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {data.combos.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">
              No users with dietary preferences yet.
            </div>
          ) : (
            <div className="divide-y">
              {data.combos.map((combo) => {
                const restrictions = combo.restrictions || [];
                const comboKey = restrictions.join(",");
                const warming = warmingCombo === comboKey;
                return (
                  <div
                    key={comboKey}
                    className="p-3 flex items-center gap-3 flex-wrap"
                    data-testid={`combo-row-${comboKey}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap gap-1 mb-1">
                        {restrictions.map((r) => (
                          <Badge key={r} variant="outline" className="text-xs capitalize">
                            {r.replace(/^no-/, "No ")}
                          </Badge>
                        ))}
                      </div>
                      <div className="text-xs text-gray-500">
                        {combo.user_count} user{combo.user_count !== 1 ? "s" : ""} ·{" "}
                        {combo.cached_entries}/{combo.total_expected} entries ({combo.coverage_pct}%)
                        {combo.sample_emails && combo.sample_emails.length > 0 && (
                          <span className="ml-1 text-gray-400">
                            · {combo.sample_emails.slice(0, 2).join(", ")}
                            {combo.sample_emails.length > 2 ? "…" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                    <StatusPill status={combo.status} />
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={warming || combo.status === "warm"}
                      onClick={() => warmCombo(restrictions)}
                      data-testid={`warm-combo-btn-${comboKey}`}
                    >
                      {warming ? (
                        <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Warming…</>
                      ) : combo.status === "warm" ? (
                        "Fully warmed"
                      ) : (
                        <><Flame className="h-3 w-3 mr-1" /> Warm now</>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-gray-400 text-center px-4 leading-snug">
        Self-healing automation: User-profile warmup 2 AM · Slot-fill health check 3 AM · Stale-cache refresh 4 AM · Empty diet combo retry every 2 hrs · Adaptive diet learning Sun 1 AM. All run automatically — manual buttons are for on-demand control.
      </p>
    </div>
  );
};

export default AdminMealCoverageTab;
